import { t } from '@owl-nest/localize'

type ApiErrors = {
  [error: string]: (message?: string) => string
}

const API_ERRORS: ApiErrors = {
  AlreadyExistsError: (message?: string) => 'An image with this lang and type already exists',
  FileTooLargeError: (message?: string) => {
    const expression = /(\d+(\.\d+)?) MB/
    let maxFileSize
    if (message) {
      const match = message.match(expression)
      maxFileSize = match?.[0]
    }
    if (maxFileSize) {
      return t('File is too large to be uploaded, maximum allowed size: %(size)s', {
        size: maxFileSize,
      })
    }
    // TODO: Log to Sentry since this means front and API are not in sync
    return t('File is too large to be uploaded. Please try again with a smaller file.')
  },
  ValueError: (message?: string) => {
    if (message?.includes('The size of a main image must be equal to or greater than 1440x297')) {
      return t('The size of the image must be equal to or greater than %(size)s.', {
        size: '1440x297',
      })
    } else if (message?.includes('The size of a main image must be equal to or greater than')) {
      return t('The size of the image must be equal to or greater than %(size)s.', {
        size: '640x360',
      })
    } else if (message?.includes('The size of a background image must be equal to or greater than')) {
      return t('The size of the image must be equal to or greater than %(size)s.', {
        size: '1440x530',
      })
    } else if (message?.includes('Video URL is invalid')) {
      return t('The URL seems to be incorrect and/or does not match any video')
    } else if (message?.includes('Image can not be decoded')) {
      return t("We couldn't process the image file. Please try again with another file.")
    } else if (message?.includes('File content does not match the file extension')) {
      return (
        t('Sorry, this file type is not supported. Please choose one of the following: %(supportedFileTypes)s', {
          supportedFileTypes: 'JPG, GIF, PNG',
        }) + t('File content does not match the file extension.')
      )
    }
    return t('Oops... An error occurred during the processing of your image file. Please retry')
  },
  UnsupportedFileFormatError: (message?: string) => {
    return t('Sorry, this file type is not supported. Please choose one of the following: %(supportedFileTypes)s', {
      supportedFileTypes: 'JPG, GIF, PNG',
    })
  },
} as const

export function getApiError(body: any): string | undefined {
  return getTranslatedApiError(formatApiError(body))
}

export function getTranslatedApiError(
  apiError: { classification: string; message?: string } | undefined,
): string | undefined {
  if (apiError === undefined) {
    return
  }

  return (
    API_ERRORS[apiError.classification](apiError.message) ||
    t('Oops... An error occurred during the processing of your image file. Please retry')
  )
}

export function formatApiError(
  body: any,
): { classification: string; message?: string; fieldNames?: string[] } | undefined {
  if (body === null || body === undefined) {
    return undefined
  }

  // Have a generic error for very old browsers
  if (!Array.isArray) return undefined

  if (Array.isArray(body)) {
    const [error] = body
    return error && { classification: error.classification, message: error.message, fieldNames: error.fieldNames }
  }

  const { errors } = body
  if (errors && Array.isArray(errors)) {
    const [error] = errors
    return error && { classification: error.classification, message: error.message, fieldNames: error.fieldNames }
  }

  if (body.classication)
    return { classification: body.classication, message: body.message, fieldNames: body.fieldNames }
  if (body.classification)
    return { classification: body.classification, message: body.message, fieldNames: body.fieldNames }

  return undefined
}
